import React, { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon, GlobeIcon, TruckIcon, HelpCircleIcon, InfoIcon, MailIcon, CheckCircleIcon, PlusIcon, Trash2Icon, FacebookIcon, InstagramIcon, TwitterIcon, MusicIcon, CopyIcon, PhoneIcon, MapPinIcon, StoreIcon, UploadIcon, MessageCircleIcon, PackageIcon, PercentIcon, UserIcon, DollarSignIcon, BellIcon, MessageSquareIcon, ScrollTextIcon, SparklesIcon } from 'lucide-react';
type ShippingMode = 'none' | 'free' | 'flat' | 'calculator';
interface WeightTier {
  id: string;
  maxWeightKg: number;
  pricePerKg: number;
}
interface FaqItem {
  id: string;
  question: string;
  answer: string;
}
export function ShopSettings() {
  const navigate = useNavigate();
  const {
    shopId
  } = useParams<{
    shopId: string;
  }>();
  const [searchParams] = useSearchParams();
  const initialSection = searchParams.get('section') || 'storefront';
  const [activeSection, setActiveSection] = useState(initialSection);
  // Domain
  const [customDomain, setCustomDomain] = useState('shop.pomelodemo.com');
  const [domainVerified, setDomainVerified] = useState(false);
  // Shipping
  const [shippingMode, setShippingMode] = useState<ShippingMode>('calculator');
  const [flatRate, setFlatRate] = useState('5.00');
  const [baseFee, setBaseFee] = useState('2.50');
  const [weightTiers, setWeightTiers] = useState<WeightTier[]>([{
    id: 't1',
    maxWeightKg: 1,
    pricePerKg: 3
  }, {
    id: 't2',
    maxWeightKg: 5,
    pricePerKg: 2
  }, {
    id: 't3',
    maxWeightKg: 20,
    pricePerKg: 1.25
  }]);
  const [zones, setZones] = useState<{
    id: string;
    name: string;
    surcharge: number;
  }[]>([{
    id: 'z1',
    name: 'Malé',
    surcharge: 0
  }, {
    id: 'z2',
    name: 'Hulhumalé',
    surcharge: 1.5
  }, {
    id: 'z3',
    name: 'Atolls — North',
    surcharge: 8
  }, {
    id: 'z4',
    name: 'Atolls — South',
    surcharge: 10
  }]);
  // FAQs
  const [faqs, setFaqs] = useState<FaqItem[]>([{
    id: 'f1',
    question: 'How long does shipping take?',
    answer: '1-2 business days within Malé. 3-5 days to atolls.'
  }, {
    id: 'f2',
    question: 'What is your return policy?',
    answer: '14-day no-questions-asked returns on unworn items.'
  }]);
  // About
  const [aboutText, setAboutText] = useState('Beach Shop is your home for quality beachwear and accessories in the Maldives. Family-owned and operated since 2018.');
  // Contact
  const [contactEmail, setContactEmail] = useState('hello@beachshop.mv');
  const [contactPhone, setContactPhone] = useState('+960 333 4455');
  const [contactAddress, setContactAddress] = useState('Boduthakurufaanu Magu, Malé 20057, Maldives');
  // Socials
  const [facebook, setFacebook] = useState('https://facebook.com/beachshop');
  const [instagram, setInstagram] = useState('https://instagram.com/beachshop');
  const [twitter, setTwitter] = useState('');
  const [tiktok, setTiktok] = useState('https://tiktok.com/@beachshop');
  const [whatsapp, setWhatsapp] = useState('https://wa.me/9601234567');
  // Migrated Settings
  const [inventoryTracking, setInventoryTracking] = useState(true);
  const [lowStockThreshold, setLowStockThreshold] = useState(5);
  const [notifyOnLowStock, setNotifyOnLowStock] = useState(false);
  const [allowBackorders, setAllowBackorders] = useState(false);
  const [hideOutOfStock, setHideOutOfStock] = useState(false);
  const [enableTaxCalculation, setEnableTaxCalculation] = useState(false);
  const [pricesIncludeTax, setPricesIncludeTax] = useState(false);
  const [taxRates, setTaxRates] = useState([{
    id: 'tx1',
    name: 'Standard Rate',
    rate: 6,
    isDefault: true
  }, {
    id: 'tx2',
    name: 'Reduced Rate',
    rate: 3,
    isDefault: false
  }, {
    id: 'tx3',
    name: 'Zero Rate',
    rate: 0,
    isDefault: false
  }]);
  const [customerFields, setCustomerFields] = useState([{
    id: 'cf1',
    name: 'Full Name',
    type: 'Text',
    required: true,
    enabled: true
  }, {
    id: 'cf2',
    name: 'Email',
    type: 'Email',
    required: true,
    enabled: true
  }, {
    id: 'cf3',
    name: 'Phone Number',
    type: 'Phone',
    required: true,
    enabled: true
  }, {
    id: 'cf4',
    name: 'Address',
    type: 'Text',
    required: true,
    enabled: true
  }, {
    id: 'cf5',
    name: 'Additional Notes',
    type: 'Textarea',
    required: false,
    enabled: true
  }]);
  const [shopFees, setShopFees] = useState([{
    id: 'f1',
    name: 'Processing Fee',
    amount: 2.5,
    type: 'Percentage',
    condition: 'Applied to all orders'
  }, {
    id: 'f2',
    name: 'Shipping Fee',
    amount: 5.0,
    type: 'Fixed',
    condition: 'Applied to orders over MVR 100'
  }]);
  const [confirmationMessage, setConfirmationMessage] = useState('Thank you for your order! We will process it shortly.');
  const [notificationEmail, setNotificationEmail] = useState('notifications@beachshop.mv');
  const [notificationPhone, setNotificationPhone] = useState('+960 333 4455');
  const [sendOrderNotifications, setSendOrderNotifications] = useState(true);
  const [sendLowStockAlerts, setSendLowStockAlerts] = useState(false);
  const [customerMessage, setCustomerMessage] = useState('Welcome to Beach Shop! Let us know if you have any questions.');
  const [enableCustomerMessages, setEnableCustomerMessages] = useState(true);
  // Inbox state (Customer Messages section)
  const [inboxSearch, setInboxSearch] = useState('');
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>('c1');
  const [newMessageText, setNewMessageText] = useState('');
  const [conversations, setConversations] = useState<{
    id: string;
    customerName: string;
    unread: number;
    messages: {
      id: string;
      sender: 'customer' | 'shop';
      text: string;
      time: string;
    }[];
  }[]>([{
    id: 'c1',
    customerName: 'Aishath Saeed',
    unread: 2,
    messages: [{
      id: 'm1',
      sender: 'customer',
      text: 'Hi! Do you ship the blue beach towel to Addu?',
      time: '2h ago'
    }, {
      id: 'm2',
      sender: 'customer',
      text: 'And how long would it take?',
      time: '2h ago'
    }]
  }, {
    id: 'c2',
    customerName: 'Ibrahim Waheed',
    unread: 0,
    messages: [{
      id: 'm1',
      sender: 'customer',
      text: 'Can I exchange the swim shorts for a different size?',
      time: '1d ago'
    }, {
      id: 'm2',
      sender: 'shop',
      text: 'Yes — within 14 days of delivery, unworn. Bring the receipt.',
      time: '1d ago'
    }, {
      id: 'm3',
      sender: 'customer',
      text: 'Perfect, thanks!',
      time: '1d ago'
    }]
  }, {
    id: 'c3',
    customerName: 'Hassan Manik',
    unread: 1,
    messages: [{
      id: 'm1',
      sender: 'customer',
      text: 'When will the Beach Towel be back in stock?',
      time: '3d ago'
    }]
  }, {
    id: 'c4',
    customerName: 'Fathimath Nadhiya',
    unread: 0,
    messages: [{
      id: 'm1',
      sender: 'customer',
      text: 'Are the sunglasses polarized?',
      time: '5d ago'
    }, {
      id: 'm2',
      sender: 'shop',
      text: 'Yes, all our sunglasses are polarized and 100% UV protected.',
      time: '5d ago'
    }]
  }, {
    id: 'c5',
    customerName: 'Mariyam Zoona',
    unread: 0,
    messages: [{
      id: 'm1',
      sender: 'customer',
      text: 'Do you do gift wrapping?',
      time: '1w ago'
    }, {
      id: 'm2',
      sender: 'shop',
      text: 'We do! It’s MVR 30 per item — selectable at checkout.',
      time: '1w ago'
    }]
  }, {
    id: 'c6',
    customerName: 'Mohamed Rasheed',
    unread: 0,
    messages: [{
      id: 'm1',
      sender: 'customer',
      text: 'Can I pick up my order from the Malé store?',
      time: '2w ago'
    }, {
      id: 'm2',
      sender: 'shop',
      text: 'Absolutely — local pickup is free, just select it at checkout.',
      time: '2w ago'
    }]
  }]);
  // Legal
  const [privacyPolicy, setPrivacyPolicy] = useState('We respect your privacy. Any personal information you share with Beach Shop — such as your name, contact details, and order history — is used solely to fulfill your orders and improve your shopping experience. We do not sell or share your information with third parties, except where required to deliver your purchase (e.g. shipping providers).');
  const [termsOfService, setTermsOfService] = useState('By placing an order with Beach Shop, you agree to the following terms: All sales are subject to product availability. Prices are listed in MVR and inclusive of applicable taxes. Returns are accepted within 14 days of delivery for unworn items in original packaging. Beach Shop reserves the right to refuse service at our discretion.');
  // Widgets
  const [showFreeShippingWidget, setShowFreeShippingWidget] = useState(true);
  const [showEasyReturnsWidget, setShowEasyReturnsWidget] = useState(true);
  const [showSecureCheckoutWidget, setShowSecureCheckoutWidget] = useState(true);
  const [showLocalShopWidget, setShowLocalShopWidget] = useState(false);
  // Free shipping threshold (pre-tax amount in MVR; 0 disables)
  const [freeShippingThreshold, setFreeShippingThreshold] = useState('500');
  // Express shipping add-on (% surcharge on top of base shipping cost)
  const [expressShippingEnabled, setExpressShippingEnabled] = useState(false);
  const [expressShippingPercent, setExpressShippingPercent] = useState('25');
  const [savedToast, setSavedToast] = useState(false);
  // Storefront
  const [heroImage, setHeroImage] = useState('https://images.unsplash.com/photo-1514282401047-d79a71a590e8?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80');
  const [shopTitle, setShopTitle] = useState('Beach Shop');
  const [tagline, setTagline] = useState('Quality beachwear and accessories');
  const [description, setStoreDescription] = useState('Your home for quality beachwear in the Maldives.');
  const addTier = () => {
    const maxWeight = weightTiers[weightTiers.length - 1]?.maxWeightKg ?? 0;
    setWeightTiers([...weightTiers, {
      id: 't' + Math.random().toString(36).slice(2, 7),
      maxWeightKg: maxWeight + 5,
      pricePerKg: 1
    }]);
  };
  const removeTier = (id: string) => setWeightTiers(weightTiers.filter(t => t.id !== id));
  const addZone = () => {
    setZones([...zones, {
      id: 'z' + Math.random().toString(36).slice(2, 7),
      name: 'New zone',
      surcharge: 0
    }]);
  };
  const removeZone = (id: string) => setZones(zones.filter(z => z.id !== id));
  const addFaq = () => setFaqs([...faqs, {
    id: 'f' + Math.random().toString(36).slice(2, 7),
    question: '',
    answer: ''
  }]);
  const removeFaq = (id: string) => setFaqs(faqs.filter(f => f.id !== id));
  const handleSave = () => {
    setSavedToast(true);
    setTimeout(() => setSavedToast(false), 2000);
  };
  const sections = [{
    id: 'storefront',
    label: 'Storefront',
    icon: StoreIcon
  }, {
    id: 'domain',
    label: 'Custom domain',
    icon: GlobeIcon
  }, {
    id: 'shipping',
    label: 'Shipping',
    icon: TruckIcon
  }, {
    id: 'inventory',
    label: 'Inventory',
    icon: PackageIcon
  }, {
    id: 'tax',
    label: 'Tax',
    icon: PercentIcon
  }, {
    id: 'customer-fields',
    label: 'Customer fields',
    icon: UserIcon
  }, {
    id: 'fees',
    label: 'Fees & charges',
    icon: DollarSignIcon
  }, {
    id: 'notifications',
    label: 'Order notifications',
    icon: BellIcon
  }, {
    id: 'messages',
    label: 'Customer messages',
    icon: MessageSquareIcon
  }, {
    id: 'faq',
    label: 'FAQ',
    icon: HelpCircleIcon
  }, {
    id: 'about',
    label: 'About',
    icon: InfoIcon
  }, {
    id: 'contact',
    label: 'Contact',
    icon: MailIcon
  }, {
    id: 'social',
    label: 'Social media',
    icon: InstagramIcon
  }, {
    id: 'widgets',
    label: 'Widgets',
    icon: SparklesIcon
  }, {
    id: 'legal',
    label: 'Legal',
    icon: ScrollTextIcon
  }];
  return <div className="max-w-[1200px] mx-auto space-y-6" data-id="element-12246">
      {/* Header */}
      <div data-id="element-12247">
        <button onClick={() => navigate(shopId ? `/shop-details?id=${shopId}` : '/shops')} className="inline-flex items-center text-sm text-gray-500 hover:text-gray-900 mb-3" data-id="element-12248">
          <ArrowLeftIcon size={14} className="mr-1.5" data-id="element-12249" />
          Back to shop
        </button>
        <div className="flex items-center justify-between" data-id="element-12250">
          <div data-id="element-12251">
            <h1 className="text-2xl font-semibold text-gray-900" data-id="element-12252">
              Shop settings · {shopTitle}
            </h1>
            <p className="text-sm text-gray-500 mt-1" data-id="element-12253">
              Configure storefront, shipping, inventory and more for this shop.
            </p>
          </div>
        </div>
      </div>

      {savedToast && <div className="fixed bottom-6 right-6 bg-gray-900 text-white px-4 py-2.5 rounded-lg text-sm shadow-lg flex items-center gap-2 z-50" data-id="element-12254">
          <CheckCircleIcon size={16} className="text-green-400" data-id="element-12255" />
          Settings saved
        </div>}

      <div className="flex flex-col lg:flex-row gap-6" data-id="element-12256">
        {/* Left Column - Navigation */}
        <div className="lg:w-[260px] flex-shrink-0" data-id="element-12257">
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden sticky top-6" data-id="element-12258">
            <div className="p-4 border-b border-gray-100 flex items-center gap-3" data-id="element-12259">
              <div className="w-10 h-10 rounded-full bg-[#FEE7E5] text-[#9F140F] flex items-center justify-center font-bold text-lg" data-id="element-12260">
                {shopTitle.charAt(0)}
              </div>
              <div className="flex-1 min-w-0" data-id="element-12261">
                <p className="text-sm font-semibold text-gray-900 truncate" data-id="element-12262">
                  {shopTitle}
                </p>
                <button onClick={() => navigate('/shops')} className="text-xs text-[#E2231A] hover:text-[#C41B14] font-medium" data-id="element-12263">
                  Switch shop
                </button>
              </div>
            </div>
            <nav className="p-2 space-y-0.5 max-h-[calc(100vh-200px)] overflow-y-auto" data-id="element-12264">
              {sections.map(section => {
              const Icon = section.icon;
              const isActive = activeSection === section.id;
              return <button key={section.id} onClick={() => setActiveSection(section.id)} className={`w-full flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${isActive ? 'bg-[#FEE7E5] text-[#9F140F]' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}`} data-id="element-12265">
                    <Icon size={16} className={isActive ? 'text-[#E2231A]' : 'text-gray-400'} data-id="element-12266" />
                    {section.label}
                  </button>;
            })}
            </nav>
          </div>
        </div>

        {/* Right Column - Content */}
        <div className="flex-1 min-w-0" data-id="element-12267">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6 flex items-start gap-2" data-id="element-12268">
            <InfoIcon size={16} className="text-blue-600 mt-0.5 flex-shrink-0" data-id="element-12269" />
            <p className="text-sm text-blue-800" data-id="element-12270">
              These settings apply only to <strong data-id="element-12271">{shopTitle}</strong>.
            </p>
          </div>

          {/* Storefront */}
          {activeSection === 'storefront' && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-12272">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2" data-id="element-12273">
                <StoreIcon size={16} className="text-gray-500" data-id="element-12274" />
                <h2 className="text-sm font-semibold text-gray-900" data-id="element-12275">
                  Storefront branding
                </h2>
              </div>
              <div className="px-6 py-5 space-y-6" data-id="element-12276">
                <div data-id="element-12277">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-12278">
                    Hero Image
                  </label>
                  <div className="relative aspect-[21/9] w-full max-w-3xl rounded-xl border-2 border-dashed border-gray-300 overflow-hidden bg-gray-50 hover:bg-gray-100 transition-colors group cursor-pointer" data-id="element-12279">
                    {heroImage ? <>
                        <img src={heroImage} alt="Hero" className="w-full h-full object-cover" data-id="element-12280" />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center" data-id="element-12281">
                          <div className="bg-white px-4 py-2 rounded-lg text-sm font-medium text-gray-900 flex items-center gap-2 shadow-sm" data-id="element-12282">
                            <UploadIcon size={16} data-id="element-12283" />
                            Change image
                          </div>
                        </div>
                      </> : <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-500" data-id="element-12284">
                        <UploadIcon size={24} className="mb-2 text-gray-400" data-id="element-12285" />
                        <span className="text-sm font-medium text-gray-900" data-id="element-12286">
                          Click to upload
                        </span>
                        <span className="text-xs mt-1" data-id="element-12287">
                          Recommended 1600×600px, JPG or PNG
                        </span>
                      </div>}
                    <input type="file" className="absolute inset-0 opacity-0 cursor-pointer" accept="image/*" onChange={e => {
                  if (e.target.files?.[0]) {
                    setHeroImage(URL.createObjectURL(e.target.files[0]));
                  }
                }} data-id="element-12288" />
                  </div>
                  {heroImage && <button onClick={() => setHeroImage('')} className="mt-2 text-xs text-red-600 hover:text-red-700 font-medium" data-id="element-12289">
                      Remove image
                    </button>}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-12290">
                  <div data-id="element-12291">
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-12292">
                      Shop Title
                    </label>
                    <input type="text" value={shopTitle} onChange={e => setShopTitle(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-12293" />
                  </div>
                  <div data-id="element-12294">
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-12295">
                      Tagline
                    </label>
                    <input type="text" value={tagline} onChange={e => setTagline(e.target.value)} placeholder="e.g. Quality beachwear and accessories" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-12296" />
                  </div>
                  <div className="md:col-span-2" data-id="element-12297">
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-12298">
                      Description
                    </label>
                    <textarea value={description} onChange={e => setStoreDescription(e.target.value)} rows={3} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-12299" />
                  </div>
                </div>
              </div>
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end" data-id="element-12300">
                <button onClick={handleSave} className="px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg shadow-sm transition-colors" data-id="element-12301">
                  Save changes
                </button>
              </div>
            </div>}

          {/* Custom domain */}
          {activeSection === 'domain' && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-12302">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2" data-id="element-12303">
                <GlobeIcon size={16} className="text-gray-500" data-id="element-12304" />
                <h2 className="text-sm font-semibold text-gray-900" data-id="element-12305">
                  Custom domain
                </h2>
              </div>
              <div className="px-6 py-5 space-y-4" data-id="element-12306">
                <p className="text-sm text-gray-500" data-id="element-12307">
                  Link your own domain so customers see your branded URL instead
                  of a default one.
                </p>
                <div className="flex flex-col sm:flex-row gap-3" data-id="element-12308">
                  <div className="flex-1" data-id="element-12309">
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-12310">
                      Your domain
                    </label>
                    <div className="flex" data-id="element-12311">
                      <span className="inline-flex items-center px-3 bg-gray-50 border border-r-0 border-gray-200 rounded-l-lg text-sm text-gray-500" data-id="element-12312">
                        https://
                      </span>
                      <input type="text" value={customDomain} onChange={e => setCustomDomain(e.target.value)} placeholder="shop.yourdomain.com" className="flex-1 px-3 py-2 border border-gray-200 rounded-r-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-12313" />
                    </div>
                  </div>
                  <div className="flex items-end" data-id="element-12314">
                    <button onClick={() => setDomainVerified(true)} className="px-4 py-2 bg-gray-900 hover:bg-gray-800 text-white text-sm font-medium rounded-lg transition-colors" data-id="element-12315">
                      Verify domain
                    </button>
                  </div>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-12316">
                  <p className="text-xs font-semibold text-blue-900 mb-2" data-id="element-12317">
                    DNS configuration
                  </p>
                  <p className="text-xs text-blue-800 mb-2" data-id="element-12318">
                    Add this CNAME record at your domain provider:
                  </p>
                  <div className="flex items-center gap-2 bg-white border border-blue-200 rounded px-3 py-2 font-mono text-xs" data-id="element-12319">
                    <span className="text-gray-500" data-id="element-12320">CNAME</span>
                    <span className="text-gray-900" data-id="element-12321">
                      {customDomain || 'shop'}
                    </span>
                    <span className="text-gray-400" data-id="element-12322">→</span>
                    <span className="text-gray-900 flex-1" data-id="element-12323">
                      shops.bilabila.app
                    </span>
                    <button onClick={() => navigator.clipboard?.writeText('shops.bilabila.app').catch(() => {})} className="text-gray-400 hover:text-gray-700" aria-label="Copy target" data-id="element-12324">
                      <CopyIcon size={12} data-id="element-12325" />
                    </button>
                  </div>
                </div>
                {domainVerified && <div className="flex items-center gap-2 text-sm text-green-700" data-id="element-12326">
                    <CheckCircleIcon size={16} data-id="element-12327" />
                    Domain verified. SSL certificate will be issued
                    automatically.
                  </div>}
              </div>
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end" data-id="element-12328">
                <button onClick={handleSave} className="px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg shadow-sm transition-colors" data-id="element-12329">
                  Save changes
                </button>
              </div>
            </div>}

          {/* Shipping */}
          {activeSection === 'shipping' && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-12330">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2" data-id="element-12331">
                <TruckIcon size={16} className="text-gray-500" data-id="element-12332" />
                <h2 className="text-sm font-semibold text-gray-900" data-id="element-12333">
                  Shipping
                </h2>
              </div>
              <div className="px-6 py-5 space-y-5" data-id="element-12334">
                {/* Mode picker */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-id="element-12335">
                  {([{
                id: 'none' as const,
                title: 'No shipping',
                desc: 'Local pickup only.'
              }, {
                id: 'free' as const,
                title: 'Free shipping',
                desc: 'No shipping fee on any order.'
              }, {
                id: 'flat' as const,
                title: 'Flat rate',
                desc: 'Same shipping fee on every order.'
              }, {
                id: 'calculator' as const,
                title: 'Calculator',
                desc: 'Cost based on weight + delivery zone.'
              }] as const).map(opt => <button key={opt.id} onClick={() => setShippingMode(opt.id)} className={`text-left p-4 rounded-lg border-2 transition-colors ${shippingMode === opt.id ? 'border-[#E2231A] bg-red-50/40' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-12336">
                      <p className="text-sm font-semibold text-gray-900" data-id="element-12337">
                        {opt.title}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5" data-id="element-12338">{opt.desc}</p>
                    </button>)}
                </div>

                {shippingMode === 'flat' && <div className="max-w-sm" data-id="element-12339">
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-12340">
                      Flat shipping rate (MVR)
                    </label>
                    <input type="number" value={flatRate} onChange={e => setFlatRate(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-12341" />
                  </div>}

                {shippingMode === 'calculator' && <div className="space-y-5" data-id="element-12342">
                    <div className="max-w-sm" data-id="element-12343">
                      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-12344">
                        Base handling fee (MVR)
                      </label>
                      <input type="number" value={baseFee} onChange={e => setBaseFee(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-12345" />
                    </div>

                    {/* Weight tiers */}
                    <div data-id="element-12346">
                      <div className="flex items-center justify-between mb-2" data-id="element-12347">
                        <p className="text-sm font-semibold text-gray-900" data-id="element-12348">
                          Weight-based pricing
                        </p>
                        <button onClick={addTier} className="inline-flex items-center gap-1.5 text-sm text-[#E2231A] hover:text-[#C41B14] font-medium" data-id="element-12349">
                          <PlusIcon size={14} data-id="element-12350" />
                          Add tier
                        </button>
                      </div>
                      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-12351">
                        <table className="w-full text-sm" data-id="element-12352">
                          <thead className="bg-gray-50 text-xs uppercase text-gray-500" data-id="element-12353">
                            <tr data-id="element-12354">
                              <th className="text-left px-4 py-2 font-medium" data-id="element-12355">
                                Up to weight (kg)
                              </th>
                              <th className="text-left px-4 py-2 font-medium" data-id="element-12356">
                                Price per kg (MVR)
                              </th>
                              <th className="w-10" data-id="element-12357"></th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100" data-id="element-12358">
                            {weightTiers.map(tier => <tr key={tier.id} data-id="element-12359">
                                <td className="px-4 py-2" data-id="element-12360">
                                  <input type="number" value={tier.maxWeightKg} onChange={e => setWeightTiers(weightTiers.map(t => t.id === tier.id ? {
                            ...t,
                            maxWeightKg: Number(e.target.value)
                          } : t))} className="w-24 px-2 py-1 border border-gray-200 rounded text-sm" data-id="element-12361" />
                                </td>
                                <td className="px-4 py-2" data-id="element-12362">
                                  <input type="number" step="0.25" value={tier.pricePerKg} onChange={e => setWeightTiers(weightTiers.map(t => t.id === tier.id ? {
                            ...t,
                            pricePerKg: Number(e.target.value)
                          } : t))} className="w-24 px-2 py-1 border border-gray-200 rounded text-sm" data-id="element-12363" />
                                </td>
                                <td className="px-4 py-2 text-right" data-id="element-12364">
                                  <button onClick={() => removeTier(tier.id)} className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded" aria-label="Remove tier" data-id="element-12365">
                                    <Trash2Icon size={14} data-id="element-12366" />
                                  </button>
                                </td>
                              </tr>)}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Zones */}
                    <div data-id="element-12367">
                      <div className="flex items-center justify-between mb-2" data-id="element-12368">
                        <p className="text-sm font-semibold text-gray-900" data-id="element-12369">
                          Delivery zones
                        </p>
                        <button onClick={addZone} className="inline-flex items-center gap-1.5 text-sm text-[#E2231A] hover:text-[#C41B14] font-medium" data-id="element-12370">
                          <PlusIcon size={14} data-id="element-12371" />
                          Add zone
                        </button>
                      </div>
                      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-12372">
                        <table className="w-full text-sm" data-id="element-12373">
                          <thead className="bg-gray-50 text-xs uppercase text-gray-500" data-id="element-12374">
                            <tr data-id="element-12375">
                              <th className="text-left px-4 py-2 font-medium" data-id="element-12376">
                                Zone name
                              </th>
                              <th className="text-left px-4 py-2 font-medium" data-id="element-12377">
                                Surcharge (MVR)
                              </th>
                              <th className="w-10" data-id="element-12378"></th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100" data-id="element-12379">
                            {zones.map(zone => <tr key={zone.id} data-id="element-12380">
                                <td className="px-4 py-2" data-id="element-12381">
                                  <input type="text" value={zone.name} onChange={e => setZones(zones.map(z => z.id === zone.id ? {
                            ...z,
                            name: e.target.value
                          } : z))} className="w-full px-2 py-1 border border-gray-200 rounded text-sm" data-id="element-12382" />
                                </td>
                                <td className="px-4 py-2" data-id="element-12383">
                                  <input type="number" value={zone.surcharge} onChange={e => setZones(zones.map(z => z.id === zone.id ? {
                            ...z,
                            surcharge: Number(e.target.value)
                          } : z))} className="w-32 px-2 py-1 border border-gray-200 rounded text-sm" data-id="element-12384" />
                                </td>
                                <td className="px-4 py-2 text-right" data-id="element-12385">
                                  <button onClick={() => removeZone(zone.id)} className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded" aria-label="Remove zone" data-id="element-12386">
                                    <Trash2Icon size={14} data-id="element-12387" />
                                  </button>
                                </td>
                              </tr>)}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>}

                {(shippingMode === 'flat' || shippingMode === 'calculator') && <div className="border-t border-gray-100 pt-5" data-id="element-12388">
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-12389">
                      Free shipping threshold (pre-tax, MVR)
                    </label>
                    <div className="flex items-center gap-2 max-w-sm" data-id="element-12390">
                      <span className="text-sm text-gray-500" data-id="element-12391">MVR</span>
                      <input type="number" min="0" step="0.01" value={freeShippingThreshold} onChange={e => setFreeShippingThreshold(e.target.value)} placeholder="0.00" className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-12392" />
                    </div>
                    <p className="text-xs text-gray-500 mt-1.5" data-id="element-12393">
                      Orders with a pre-tax subtotal at or above this amount
                      qualify for free shipping. Set to 0 to disable.
                    </p>
                  </div>}

                {(shippingMode === 'flat' || shippingMode === 'calculator') && <div className="border-t border-gray-100 pt-5" data-id="element-12394">
                    <div className="flex items-center justify-between mb-2" data-id="element-12395">
                      <div data-id="element-12396">
                        <p className="text-sm font-medium text-gray-900" data-id="element-12397">
                          Express shipping add-on (optional)
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5" data-id="element-12398">
                          Let customers upgrade to express delivery for a
                          percentage surcharge on the shipping fee.
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer" data-id="element-12399">
                        <input type="checkbox" className="sr-only peer" checked={expressShippingEnabled} onChange={e => setExpressShippingEnabled(e.target.checked)} data-id="element-12400" />
                        <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-12401"></div>
                      </label>
                    </div>
                    {expressShippingEnabled && <div className="max-w-sm mt-3" data-id="element-12402">
                        <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-12403">
                          Express surcharge
                        </label>
                        <div className="flex items-center gap-2" data-id="element-12404">
                          <input type="number" min="0" step="1" value={expressShippingPercent} onChange={e => setExpressShippingPercent(e.target.value)} placeholder="25" className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-12405" />
                          <span className="text-sm text-gray-500" data-id="element-12406">%</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1.5" data-id="element-12407">
                          Applied as a percentage on top of the calculated
                          shipping cost when the customer selects Express at
                          checkout.
                        </p>
                      </div>}
                  </div>}
              </div>
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end" data-id="element-12408">
                <button onClick={handleSave} className="px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg shadow-sm transition-colors" data-id="element-12409">
                  Save changes
                </button>
              </div>
            </div>}

          {/* Inventory */}
          {activeSection === 'inventory' && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-12410">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2" data-id="element-12411">
                <PackageIcon size={16} className="text-gray-500" data-id="element-12412" />
                <h2 className="text-sm font-semibold text-gray-900" data-id="element-12413">
                  Inventory Management
                </h2>
              </div>
              <div className="px-6 py-5 space-y-6" data-id="element-12414">
                <div className="flex items-center justify-between" data-id="element-12415">
                  <div data-id="element-12416">
                    <p className="text-sm font-medium text-gray-900" data-id="element-12417">
                      Enable Stock Management
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-12418">
                      Track inventory levels for products in this shop
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer" data-id="element-12419">
                    <input type="checkbox" className="sr-only peer" checked={inventoryTracking} onChange={e => setInventoryTracking(e.target.checked)} data-id="element-12420" />
                    <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-12421"></div>
                  </label>
                </div>

                <div data-id="element-12422">
                  <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-12423">
                    Low Stock Threshold
                  </label>
                  <input type="number" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" value={lowStockThreshold} onChange={e => setLowStockThreshold(parseInt(e.target.value))} data-id="element-12424" />
                  <p className="text-xs text-gray-500 mt-1" data-id="element-12425">
                    Products with stock below this number will be marked as low
                    stock
                  </p>
                </div>

                <div className="flex items-center justify-between" data-id="element-12426">
                  <div data-id="element-12427">
                    <p className="text-sm font-medium text-gray-900" data-id="element-12428">
                      Notify on Low Stock
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-12429">
                      Receive notifications when products reach low stock
                      threshold
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer" data-id="element-12430">
                    <input type="checkbox" className="sr-only peer" checked={notifyOnLowStock} onChange={e => setNotifyOnLowStock(e.target.checked)} data-id="element-12431" />
                    <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-12432"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between" data-id="element-12433">
                  <div data-id="element-12434">
                    <p className="text-sm font-medium text-gray-900" data-id="element-12435">
                      Allow Backorders
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-12436">
                      Allow customers to purchase products that are out of stock
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer" data-id="element-12437">
                    <input type="checkbox" className="sr-only peer" checked={allowBackorders} onChange={e => setAllowBackorders(e.target.checked)} data-id="element-12438" />
                    <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-12439"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between" data-id="element-12440">
                  <div data-id="element-12441">
                    <p className="text-sm font-medium text-gray-900" data-id="element-12442">
                      Hide Out of Stock Products
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-12443">
                      Automatically hide products when they are out of stock
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer" data-id="element-12444">
                    <input type="checkbox" className="sr-only peer" checked={hideOutOfStock} onChange={e => setHideOutOfStock(e.target.checked)} data-id="element-12445" />
                    <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-12446"></div>
                  </label>
                </div>
              </div>
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end" data-id="element-12447">
                <button onClick={handleSave} className="px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg shadow-sm transition-colors" data-id="element-12448">
                  Save changes
                </button>
              </div>
            </div>}

          {/* Tax */}
          {activeSection === 'tax' && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-12449">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2" data-id="element-12450">
                <PercentIcon size={16} className="text-gray-500" data-id="element-12451" />
                <h2 className="text-sm font-semibold text-gray-900" data-id="element-12452">
                  Tax Configuration
                </h2>
              </div>
              <div className="px-6 py-5 space-y-6" data-id="element-12453">
                <div className="flex items-center justify-between" data-id="element-12454">
                  <div data-id="element-12455">
                    <p className="text-sm font-medium text-gray-900" data-id="element-12456">
                      Enable Tax Calculation
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-12457">
                      Calculate and apply taxes to orders
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer" data-id="element-12458">
                    <input type="checkbox" className="sr-only peer" checked={enableTaxCalculation} onChange={e => setEnableTaxCalculation(e.target.checked)} data-id="element-12459" />
                    <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-12460"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between" data-id="element-12461">
                  <div data-id="element-12462">
                    <p className="text-sm font-medium text-gray-900" data-id="element-12463">
                      Prices Include Tax
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-12464">
                      Product prices already include tax
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer" data-id="element-12465">
                    <input type="checkbox" className="sr-only peer" checked={pricesIncludeTax} onChange={e => setPricesIncludeTax(e.target.checked)} data-id="element-12466" />
                    <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-12467"></div>
                  </label>
                </div>

                <div data-id="element-12468">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-12469">
                    Tax Rates
                  </h4>
                  <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-12470">
                    <table className="w-full" data-id="element-12471">
                      <thead className="bg-gray-50 border-b border-gray-200" data-id="element-12472">
                        <tr data-id="element-12473">
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-12474">
                            Name
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-12475">
                            Rate (%)
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-12476">
                            Default
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase" data-id="element-12477">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 bg-white" data-id="element-12478">
                        {taxRates.map(rate => <tr key={rate.id} className="hover:bg-gray-50" data-id="element-12479">
                            <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-12480">
                              {rate.name}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600" data-id="element-12481">
                              {rate.rate}%
                            </td>
                            <td className="px-4 py-3" data-id="element-12482">
                              {rate.isDefault ? <span className="text-green-600 flex items-center gap-1" data-id="element-12483">
                                  <CheckCircleIcon size={16} data-id="element-12484" />
                                </span> : <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-12485">
                                  Set as default
                                </button>}
                            </td>
                            <td className="px-4 py-3 text-right" data-id="element-12486">
                              <button className="text-sm text-blue-600 hover:text-blue-700 mr-3" data-id="element-12487">
                                Edit
                              </button>
                              <button className="text-sm text-red-600 hover:text-red-700" data-id="element-12488">
                                Delete
                              </button>
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>
                  <button className="mt-3 flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-12489">
                    <PlusIcon size={16} data-id="element-12490" />
                    Add Tax Rate
                  </button>
                </div>
              </div>
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end" data-id="element-12491">
                <button onClick={handleSave} className="px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg shadow-sm transition-colors" data-id="element-12492">
                  Save changes
                </button>
              </div>
            </div>}

          {/* Customer Fields */}
          {activeSection === 'customer-fields' && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-12493">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2" data-id="element-12494">
                <UserIcon size={16} className="text-gray-500" data-id="element-12495" />
                <h2 className="text-sm font-semibold text-gray-900" data-id="element-12496">
                  Customer Information Fields
                </h2>
              </div>
              <div className="px-6 py-5" data-id="element-12497">
                <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-12498">
                  <table className="w-full" data-id="element-12499">
                    <thead className="bg-gray-50 border-b border-gray-200" data-id="element-12500">
                      <tr data-id="element-12501">
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-12502">
                          Field Name
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-12503">
                          Type
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase" data-id="element-12504">
                          Required
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase" data-id="element-12505">
                          Enabled
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase" data-id="element-12506">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white" data-id="element-12507">
                      {customerFields.map(field => <tr key={field.id} className="hover:bg-gray-50" data-id="element-12508">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-12509">
                            {field.name}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600" data-id="element-12510">
                            {field.type}
                          </td>
                          <td className="px-4 py-3 text-center" data-id="element-12511">
                            <input type="checkbox" checked={field.required} className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" readOnly data-id="element-12512" />
                          </td>
                          <td className="px-4 py-3 text-center" data-id="element-12513">
                            <input type="checkbox" checked={field.enabled} className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" readOnly data-id="element-12514" />
                          </td>
                          <td className="px-4 py-3 text-right" data-id="element-12515">
                            <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-12516">
                              Edit
                            </button>
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>
                <button className="mt-3 flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-12517">
                  <PlusIcon size={16} data-id="element-12518" />
                  Add Custom Field
                </button>
              </div>
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end" data-id="element-12519">
                <button onClick={handleSave} className="px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg shadow-sm transition-colors" data-id="element-12520">
                  Save changes
                </button>
              </div>
            </div>}

          {/* Fees & Charges */}
          {activeSection === 'fees' && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-12521">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2" data-id="element-12522">
                <DollarSignIcon size={16} className="text-gray-500" data-id="element-12523" />
                <h2 className="text-sm font-semibold text-gray-900" data-id="element-12524">
                  Custom Fees & Service Charges
                </h2>
              </div>
              <div className="px-6 py-5" data-id="element-12525">
                <div className="border border-gray-200 rounded-lg overflow-hidden mb-4" data-id="element-12526">
                  <table className="w-full" data-id="element-12527">
                    <thead className="bg-gray-50 border-b border-gray-200" data-id="element-12528">
                      <tr data-id="element-12529">
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-12530">
                          Fee Name
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-12531">
                          Amount
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-12532">
                          Type
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-12533">
                          Conditions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white" data-id="element-12534">
                      {shopFees.map(fee => <tr key={fee.id} className="hover:bg-gray-50" data-id="element-12535">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-12536">
                            {fee.name}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900" data-id="element-12537">
                            {fee.type === 'Percentage' ? `${fee.amount}%` : `MVR ${fee.amount.toFixed(2)}`}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600" data-id="element-12538">
                            {fee.type}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500" data-id="element-12539">
                            {fee.condition}
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>
                <button className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-12540">
                  <PlusIcon size={16} data-id="element-12541" />
                  Add Fee
                </button>
              </div>
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end" data-id="element-12542">
                <button onClick={handleSave} className="px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg shadow-sm transition-colors" data-id="element-12543">
                  Save changes
                </button>
              </div>
            </div>}

          {/* Order Notifications */}
          {activeSection === 'notifications' && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-12544">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2" data-id="element-12545">
                <BellIcon size={16} className="text-gray-500" data-id="element-12546" />
                <h2 className="text-sm font-semibold text-gray-900" data-id="element-12547">
                  Order Notifications
                </h2>
              </div>
              <div className="px-6 py-5 space-y-5" data-id="element-12548">
                <div data-id="element-12549">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-12550">
                    Order Confirmation Message
                  </label>
                  <textarea className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" rows={3} value={confirmationMessage} onChange={e => setConfirmationMessage(e.target.value)} data-id="element-12551" />
                  <p className="text-xs text-gray-500 mt-1" data-id="element-12552">
                    This message will be displayed to customers after they
                    complete an order
                  </p>
                </div>

                <div data-id="element-12553">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-12554">
                    Notification Email
                  </label>
                  <input type="email" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" value={notificationEmail} onChange={e => setNotificationEmail(e.target.value)} data-id="element-12555" />
                  <p className="text-xs text-gray-500 mt-1" data-id="element-12556">
                    Email address where order notifications will be sent
                  </p>
                </div>

                <div data-id="element-12557">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-12558">
                    Notification Phone Number
                  </label>
                  <input type="tel" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" value={notificationPhone} onChange={e => setNotificationPhone(e.target.value)} data-id="element-12559" />
                  <p className="text-xs text-gray-500 mt-1" data-id="element-12560">
                    Phone number where order notifications will be sent
                  </p>
                </div>

                <div className="space-y-3 pt-2" data-id="element-12561">
                  <div className="flex items-center justify-between border border-gray-200 rounded-lg px-4 py-3" data-id="element-12562">
                    <div data-id="element-12563">
                      <p className="text-sm font-medium text-gray-900" data-id="element-12564">
                        Send Order Notifications
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5" data-id="element-12565">
                        Receive notifications for new orders
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer" data-id="element-12566">
                      <input type="checkbox" className="sr-only peer" checked={sendOrderNotifications} onChange={e => setSendOrderNotifications(e.target.checked)} data-id="element-12567" />
                      <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-12568"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between border border-gray-200 rounded-lg px-4 py-3" data-id="element-12569">
                    <div data-id="element-12570">
                      <p className="text-sm font-medium text-gray-900" data-id="element-12571">
                        Send Low Stock Alerts
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5" data-id="element-12572">
                        Receive notifications when products reach low stock
                        levels
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer" data-id="element-12573">
                      <input type="checkbox" className="sr-only peer" checked={sendLowStockAlerts} onChange={e => setSendLowStockAlerts(e.target.checked)} data-id="element-12574" />
                      <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-12575"></div>
                    </label>
                  </div>
                </div>
              </div>
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end" data-id="element-12576">
                <button onClick={handleSave} className="px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg shadow-sm transition-colors" data-id="element-12577">
                  Save changes
                </button>
              </div>
            </div>}

          {/* Customer Messages */}
          {activeSection === 'messages' && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col h-[640px]" data-id="element-12578">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between shrink-0" data-id="element-12579">
                <div className="flex items-center gap-2" data-id="element-12580">
                  <MessageSquareIcon size={16} className="text-gray-500" data-id="element-12581" />
                  <h2 className="text-sm font-semibold text-gray-900" data-id="element-12582">
                    Customer Messages
                  </h2>
                </div>
                <div className="flex items-center gap-2" data-id="element-12583">
                  <span className="text-xs text-gray-500" data-id="element-12584">
                    Accept new messages
                  </span>
                  <label className="relative inline-flex items-center cursor-pointer" data-id="element-12585">
                    <input type="checkbox" className="sr-only peer" checked={enableCustomerMessages} onChange={e => setEnableCustomerMessages(e.target.checked)} data-id="element-12586" />
                    <div className="w-9 h-5 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-4" data-id="element-12587"></div>
                  </label>
                </div>
              </div>

              <div className="flex flex-1 min-h-0" data-id="element-12588">
                {/* Inbox list */}
                <div className="w-[280px] border-r border-gray-100 flex flex-col bg-gray-50/40" data-id="element-12589">
                  <div className="p-3 border-b border-gray-100" data-id="element-12590">
                    <input type="text" placeholder="Search messages..." value={inboxSearch} onChange={e => setInboxSearch(e.target.value)} className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] bg-white" data-id="element-12591" />
                  </div>
                  <div className="flex-1 overflow-y-auto" data-id="element-12592">
                    {conversations.filter(c => c.customerName.toLowerCase().includes(inboxSearch.toLowerCase())).map(conv => {
                  const last = conv.messages[conv.messages.length - 1];
                  const isActive = selectedConversationId === conv.id;
                  return <button key={conv.id} onClick={() => setSelectedConversationId(conv.id)} className={`w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-white transition-colors ${isActive ? 'bg-white border-l-2 border-l-[#E2231A]' : 'border-l-2 border-l-transparent'}`} data-id="element-12593">
                            <div className="flex items-center justify-between mb-1" data-id="element-12594">
                              <span className="text-sm font-semibold text-gray-900 truncate" data-id="element-12595">
                                {conv.customerName}
                              </span>
                              <span className="text-[10px] text-gray-400 whitespace-nowrap ml-2" data-id="element-12596">
                                {last.time}
                              </span>
                            </div>
                            <div className="flex items-center justify-between gap-2" data-id="element-12597">
                              <p className="text-xs text-gray-500 truncate flex-1" data-id="element-12598">
                                {last.sender === 'shop' ? 'You: ' : ''}
                                {last.text}
                              </p>
                              {conv.unread > 0 && <span className="inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 text-[10px] font-bold text-white bg-[#E2231A] rounded-full" data-id="element-12599">
                                  {conv.unread}
                                </span>}
                            </div>
                          </button>;
                })}
                    {conversations.length === 0 && <p className="text-xs text-gray-400 text-center py-8 px-4" data-id="element-12600">
                        No conversations yet. Once customers reach out, they'll
                        appear here.
                      </p>}
                  </div>
                </div>

                {/* Thread */}
                <div className="flex-1 flex flex-col bg-white min-w-0" data-id="element-12601">
                  {selectedConversationId ? (() => {
                const conv = conversations.find(c => c.id === selectedConversationId);
                if (!conv) return null;
                return <>
                          <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-3 shrink-0" data-id="element-12602">
                            <div className="w-9 h-9 rounded-full bg-[#FEE7E5] text-[#9F140F] flex items-center justify-center font-semibold text-sm" data-id="element-12603">
                              {conv.customerName.charAt(0)}
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-12604">
                              <p className="text-sm font-semibold text-gray-900 truncate" data-id="element-12605">
                                {conv.customerName}
                              </p>
                              <p className="text-xs text-gray-500" data-id="element-12606">Customer</p>
                            </div>
                          </div>
                          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3 bg-gray-50/30" data-id="element-12607">
                            {conv.messages.map(msg => {
                      const isShop = msg.sender === 'shop';
                      return <div key={msg.id} className={`flex ${isShop ? 'justify-end' : 'justify-start'}`} data-id="element-12608">
                                  <div className="max-w-[75%]" data-id="element-12609">
                                    <div className={`rounded-2xl px-3.5 py-2 text-sm ${isShop ? 'bg-[#E2231A] text-white rounded-br-sm' : 'bg-white text-gray-900 border border-gray-200 rounded-bl-sm'}`} data-id="element-12610">
                                      {msg.text}
                                    </div>
                                    <p className={`text-[10px] text-gray-400 mt-1 ${isShop ? 'text-right' : 'text-left'}`} data-id="element-12611">
                                      {msg.time}
                                    </p>
                                  </div>
                                </div>;
                    })}
                          </div>
                          <div className="p-3 border-t border-gray-100 shrink-0" data-id="element-12612">
                            <div className="flex items-end gap-2" data-id="element-12613">
                              <textarea value={newMessageText} onChange={e => setNewMessageText(e.target.value)} placeholder="Type a reply..." rows={1} onKeyDown={e => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          if (!newMessageText.trim()) return;
                          setConversations(prev => prev.map(c => c.id === selectedConversationId ? {
                            ...c,
                            unread: 0,
                            messages: [...c.messages, {
                              id: 'm' + Date.now(),
                              sender: 'shop',
                              text: newMessageText,
                              time: 'Just now'
                            }]
                          } : c));
                          setNewMessageText('');
                        }
                      }} className="flex-1 max-h-32 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] resize-none" data-id="element-12614" />
                              <button onClick={() => {
                        if (!newMessageText.trim()) return;
                        setConversations(prev => prev.map(c => c.id === selectedConversationId ? {
                          ...c,
                          unread: 0,
                          messages: [...c.messages, {
                            id: 'm' + Date.now(),
                            sender: 'shop',
                            text: newMessageText,
                            time: 'Just now'
                          }]
                        } : c));
                        setNewMessageText('');
                      }} className="px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg transition-colors" data-id="element-12615">
                                Send
                              </button>
                            </div>
                            <p className="text-[10px] text-gray-400 mt-1.5" data-id="element-12616">
                              Press Enter to send, Shift+Enter for a new line.
                            </p>
                          </div>
                        </>;
              })() : <div className="flex-1 flex flex-col items-center justify-center text-gray-400 px-6 text-center" data-id="element-12617">
                      <MessageSquareIcon size={40} className="mb-3 opacity-30" data-id="element-12618" />
                      <p className="text-sm font-medium text-gray-900" data-id="element-12619">
                        No conversation selected
                      </p>
                      <p className="text-xs mt-1 text-gray-500 max-w-xs" data-id="element-12620">
                        Choose a customer from the list on the left to view the
                        thread and reply.
                      </p>
                    </div>}
                </div>
              </div>
            </div>}

          {/* FAQ */}
          {activeSection === 'faq' && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-12621">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between" data-id="element-12622">
                <div className="flex items-center gap-2" data-id="element-12623">
                  <HelpCircleIcon size={16} className="text-gray-500" data-id="element-12624" />
                  <h2 className="text-sm font-semibold text-gray-900" data-id="element-12625">FAQ</h2>
                </div>
                <button onClick={addFaq} className="inline-flex items-center gap-1.5 text-sm text-[#E2231A] hover:text-[#C41B14] font-medium" data-id="element-12626">
                  <PlusIcon size={14} data-id="element-12627" />
                  Add question
                </button>
              </div>
              <div className="px-6 py-5 space-y-4" data-id="element-12628">
                {faqs.map((faq, i) => <div key={faq.id} className="border border-gray-200 rounded-lg p-4 space-y-3" data-id="element-12629">
                    <div className="flex items-start justify-between gap-2" data-id="element-12630">
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12631">
                        Question {i + 1}
                      </label>
                      <button onClick={() => removeFaq(faq.id)} className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded" aria-label="Remove" data-id="element-12632">
                        <Trash2Icon size={14} data-id="element-12633" />
                      </button>
                    </div>
                    <input type="text" value={faq.question} onChange={e => setFaqs(faqs.map(f => f.id === faq.id ? {
                ...f,
                question: e.target.value
              } : f))} placeholder="e.g. Do you ship internationally?" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-12634" />
                    <textarea value={faq.answer} onChange={e => setFaqs(faqs.map(f => f.id === faq.id ? {
                ...f,
                answer: e.target.value
              } : f))} placeholder="Provide a clear, helpful answer..." rows={3} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-12635" />
                  </div>)}
                {faqs.length === 0 && <p className="text-sm text-gray-500 text-center py-4" data-id="element-12636">
                    No FAQs yet. Add your first question.
                  </p>}
              </div>
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end" data-id="element-12637">
                <button onClick={handleSave} className="px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg shadow-sm transition-colors" data-id="element-12638">
                  Save changes
                </button>
              </div>
            </div>}

          {/* About */}
          {activeSection === 'about' && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-12639">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2" data-id="element-12640">
                <InfoIcon size={16} className="text-gray-500" data-id="element-12641" />
                <h2 className="text-sm font-semibold text-gray-900" data-id="element-12642">About</h2>
              </div>
              <div className="px-6 py-5" data-id="element-12643">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-12644">
                  About your shop
                </label>
                <textarea value={aboutText} onChange={e => setAboutText(e.target.value)} rows={6} placeholder="Tell customers about your shop, mission and what makes you unique..." className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-12645" />
                <p className="text-xs text-gray-400 mt-1.5" data-id="element-12646">
                  Appears on your shop's About page and in storefront footer.
                </p>
              </div>
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end" data-id="element-12647">
                <button onClick={handleSave} className="px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg shadow-sm transition-colors" data-id="element-12648">
                  Save changes
                </button>
              </div>
            </div>}

          {/* Contact */}
          {activeSection === 'contact' && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-12649">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2" data-id="element-12650">
                <MailIcon size={16} className="text-gray-500" data-id="element-12651" />
                <h2 className="text-sm font-semibold text-gray-900" data-id="element-12652">
                  Contact details
                </h2>
              </div>
              <div className="px-6 py-5 grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-12653">
                <div data-id="element-12654">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-12655">
                    Contact email
                  </label>
                  <div className="relative" data-id="element-12656">
                    <MailIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-12657" />
                    <input type="email" value={contactEmail} onChange={e => setContactEmail(e.target.value)} className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-12658" />
                  </div>
                </div>
                <div data-id="element-12659">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-12660">
                    Contact phone
                  </label>
                  <div className="relative" data-id="element-12661">
                    <PhoneIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-12662" />
                    <input type="tel" value={contactPhone} onChange={e => setContactPhone(e.target.value)} className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-12663" />
                  </div>
                </div>
                <div className="md:col-span-2" data-id="element-12664">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-12665">
                    Address
                  </label>
                  <div className="relative" data-id="element-12666">
                    <MapPinIcon size={14} className="absolute left-3 top-3 text-gray-400" data-id="element-12667" />
                    <textarea value={contactAddress} onChange={e => setContactAddress(e.target.value)} rows={2} className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-12668" />
                  </div>
                </div>
              </div>
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end" data-id="element-12669">
                <button onClick={handleSave} className="px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg shadow-sm transition-colors" data-id="element-12670">
                  Save changes
                </button>
              </div>
            </div>}

          {/* Social media */}
          {activeSection === 'social' && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-12671">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2" data-id="element-12672">
                <InstagramIcon size={16} className="text-gray-500" data-id="element-12673" />
                <h2 className="text-sm font-semibold text-gray-900" data-id="element-12674">
                  Social media
                </h2>
              </div>
              <div className="px-6 py-5 space-y-3" data-id="element-12675">
                {[{
              icon: FacebookIcon,
              label: 'Facebook',
              value: facebook,
              set: setFacebook,
              color: 'text-[#1877F2]',
              type: 'url'
            }, {
              icon: InstagramIcon,
              label: 'Instagram',
              value: instagram,
              set: setInstagram,
              color: 'text-[#E4405F]',
              type: 'url'
            }, {
              icon: MessageCircleIcon,
              label: 'WhatsApp',
              value: whatsapp,
              set: setWhatsapp,
              color: 'text-[#25D366]',
              type: 'tel'
            }, {
              icon: () => <span className="flex items-center justify-center w-full h-full font-bold text-white bg-black rounded-lg" data-id="element-12676">
                        𝕏
                      </span>,
              label: 'X.com',
              value: twitter,
              set: setTwitter,
              color: '',
              type: 'url'
            }, {
              icon: MusicIcon,
              label: 'TikTok',
              value: tiktok,
              set: setTiktok,
              color: 'text-gray-900',
              type: 'url'
            }].map(s => {
              const Icon = s.icon;
              return <div key={s.label} className="flex items-center gap-3" data-id="element-12677">
                      <div className={`w-9 h-9 rounded-lg bg-gray-50 border border-gray-200 flex items-center justify-center flex-shrink-0 ${s.color}`} data-id="element-12678">
                        <Icon size={16} data-id="element-12679" />
                      </div>
                      <div className="flex-1" data-id="element-12680">
                        <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-12681">
                          {s.label}
                        </label>
                        <input type={s.type} value={s.value} onChange={e => s.set(e.target.value)} placeholder={s.type === 'tel' ? '+960 123 4567' : `https://${s.label.toLowerCase().replace(/[^a-z]/g, '')}.com/your-handle`} className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-12682" />
                      </div>
                    </div>;
            })}
              </div>
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end" data-id="element-12683">
                <button onClick={handleSave} className="px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg shadow-sm transition-colors" data-id="element-12684">
                  Save changes
                </button>
              </div>
            </div>}

          {/* Widgets */}
          {activeSection === 'widgets' && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-12685">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2" data-id="element-12686">
                <SparklesIcon size={16} className="text-gray-500" data-id="element-12687" />
                <h2 className="text-sm font-semibold text-gray-900" data-id="element-12688">
                  Storefront widgets
                </h2>
              </div>
              <div className="px-6 py-5 space-y-3" data-id="element-12689">
                <p className="text-sm text-gray-500 -mt-1 mb-2" data-id="element-12690">
                  Choose which trust-building widgets appear on your product
                  pages and checkout to reassure shoppers.
                </p>
                {[{
              label: 'Free shipping widget',
              desc: 'Highlight free shipping eligibility on product pages.',
              value: showFreeShippingWidget,
              set: setShowFreeShippingWidget
            }, {
              label: 'Easy returns widget',
              desc: 'Show your returns policy as a reassurance badge.',
              value: showEasyReturnsWidget,
              set: setShowEasyReturnsWidget
            }, {
              label: 'Secure checkout widget',
              desc: 'Display SSL/payment security icons at checkout.',
              value: showSecureCheckoutWidget,
              set: setShowSecureCheckoutWidget
            }, {
              label: 'Local shop widget',
              desc: 'Show a "Locally owned & operated in the Maldives" badge.',
              value: showLocalShopWidget,
              set: setShowLocalShopWidget
            }].map(w => <div key={w.label} className="flex items-center justify-between border border-gray-200 rounded-lg px-4 py-3" data-id="element-12691">
                    <div data-id="element-12692">
                      <p className="text-sm font-medium text-gray-900" data-id="element-12693">
                        {w.label}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5" data-id="element-12694">{w.desc}</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer" data-id="element-12695">
                      <input type="checkbox" className="sr-only peer" checked={w.value} onChange={e => w.set(e.target.checked)} data-id="element-12696" />
                      <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-12697"></div>
                    </label>
                  </div>)}
              </div>
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end" data-id="element-12698">
                <button onClick={handleSave} className="px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg shadow-sm transition-colors" data-id="element-12699">
                  Save changes
                </button>
              </div>
            </div>}

          {/* Legal */}
          {activeSection === 'legal' && <div className="space-y-6" data-id="element-12700">
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-12701">
                <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2" data-id="element-12702">
                  <ScrollTextIcon size={16} className="text-gray-500" data-id="element-12703" />
                  <h2 className="text-sm font-semibold text-gray-900" data-id="element-12704">
                    Privacy policy
                  </h2>
                </div>
                <div className="px-6 py-5" data-id="element-12705">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-12706">
                    Privacy policy text
                  </label>
                  <textarea value={privacyPolicy} onChange={e => setPrivacyPolicy(e.target.value)} rows={10} placeholder="Explain how you collect, use, and protect customer data..." className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-12707" />
                  <p className="text-xs text-gray-400 mt-1.5" data-id="element-12708">
                    Shown on your shop's Privacy Policy page and linked from the
                    storefront footer.
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-12709">
                <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2" data-id="element-12710">
                  <ScrollTextIcon size={16} className="text-gray-500" data-id="element-12711" />
                  <h2 className="text-sm font-semibold text-gray-900" data-id="element-12712">
                    Terms of service
                  </h2>
                </div>
                <div className="px-6 py-5" data-id="element-12713">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-12714">
                    Terms of service text
                  </label>
                  <textarea value={termsOfService} onChange={e => setTermsOfService(e.target.value)} rows={10} placeholder="Outline the terms customers agree to when buying from your shop..." className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-12715" />
                  <p className="text-xs text-gray-400 mt-1.5" data-id="element-12716">
                    Shown on your shop's Terms page and linked from checkout.
                  </p>
                </div>
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end" data-id="element-12717">
                  <button onClick={handleSave} className="px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg shadow-sm transition-colors" data-id="element-12718">
                    Save changes
                  </button>
                </div>
              </div>
            </div>}
        </div>
      </div>
    </div>;
}